import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class ProductCouponOffer extends HTMLElement {
  connectedCallback() {
    this.applyButton = this.querySelector('.product-coupon-offer__apply');
    this.appliedStatus = this.querySelector('.product-coupon-offer__applied');
    this.confetti = this.querySelector('.product-coupon-offer__confetti');
    this.confettiPlayer = this.querySelector('.product-coupon-offer__confetti-player');
    this.discountText = this.querySelector('[data-coupon-discount]');
    this.discountedPriceText = this.querySelector('[data-coupon-price]');
    this.section = this.closest('.shopify-section');
    this.productDetails = this.closest('.product-details');
    this.productInformation = this.closest('.product-information');
    this.isApplied = false;

    this.applyButton?.addEventListener('click', this.applyCoupon);
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    this.updateDiscountedPriceDataset();
    this.loadDotLottiePlayer();
  }

  disconnectedCallback() {
    this.applyButton?.removeEventListener('click', this.applyCoupon);
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  applyCoupon = () => {
    if (this.updatePriceBlock()) {
      this.storeCouponCode();
      this.isApplied = true;
      this.toggleButtons();
      this.playConfetti();
    }
  };

  async playConfetti() {
    if (!this.confetti || !this.confettiPlayer) return;

    this.confetti.hidden = false;
    this.confetti.classList.remove('product-coupon-offer__confetti--playing');
    this.confetti.offsetHeight;
    this.confetti.classList.add('product-coupon-offer__confetti--playing');

    try {
      await this.loadDotLottiePlayer();
      this.confettiPlayer.stop?.();
      this.confettiPlayer.play?.();
    } catch (error) {}

    window.setTimeout(() => {
      if (this.confetti) this.confetti.hidden = true;
    }, 4200);
  }

  async loadDotLottiePlayer() {
    if (customElements.get('dotlottie-player')) return;
    if (!window.__cozyDotLottiePlayerLoading) {
      window.__cozyDotLottiePlayerLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://unpkg.com/@dotlottie/player-component@2.7.12/dist/dotlottie-player.mjs';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    await window.__cozyDotLottiePlayerLoading;
    await customElements.whenDefined('dotlottie-player');
  }

  storeCouponCode() {
    if (!this.dataset.couponCode) return;

    try {
      window.localStorage.setItem('cozy:autoDiscountCode', this.dataset.couponCode);
    } catch (error) {}
  }

  handleVariantUpdate = () => {
    window.requestAnimationFrame(() => {
      this.updateDiscountedPriceDataset();
      if (this.isApplied) {
        this.updatePriceBlock();
      }
    });
  };

  toggleButtons() {
    if (this.applyButton) this.applyButton.hidden = this.isApplied;
    if (this.appliedStatus) this.appliedStatus.hidden = !this.isApplied;
  }

  getPriceContainer() {
    return (
      this.productDetails?.querySelector('product-price [ref="priceContainer"]') ??
      this.productInformation?.querySelector('.product-details product-price [ref="priceContainer"]') ??
      this.closest('[data-testid="product-information"]')?.querySelector('.product-details product-price [ref="priceContainer"]') ??
      document.querySelector('.product-details product-price [ref="priceContainer"]')
    );
  }

  getCurrentPriceAmount() {
    const amount = Number(this.getPriceContainer()?.dataset.currentPriceAmount);
    return Number.isFinite(amount) ? amount : 0;
  }

  getDiscountAmount() {
    const percentage = Number.parseFloat(this.dataset.discountPercentage);
    if (!Number.isFinite(percentage)) return 0;

    return Math.round((this.getCurrentPriceAmount() * percentage) / 100);
  }

  getDiscountedAmount() {
    return Math.max(this.getCurrentPriceAmount() - this.getDiscountAmount(), 0);
  }

  format(amount) {
    return formatMoney(amount, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'INR');
  }

  updateDiscountedPriceDataset() {
    this.dataset.discountedPrice = this.format(this.getDiscountedAmount());
    if (this.discountText) this.discountText.textContent = this.format(this.getDiscountAmount());
    if (this.discountedPriceText) this.discountedPriceText.textContent = this.dataset.discountedPrice;
  }

  updatePriceBlock() {
    const priceContainer = this.getPriceContainer();
    const currentWrap = priceContainer?.querySelector('.price-current-wrap');
    const priceEl = currentWrap?.querySelector('.price') ?? currentWrap;
    if (!priceContainer || !currentWrap || !priceEl) return false;

    const originalPrice =
      priceEl.dataset.originalCouponPrice ||
      priceEl.querySelector('.coupon-original-price')?.textContent?.trim() ||
      priceEl.textContent?.trim() ||
      '';
    priceEl.dataset.originalCouponPrice = originalPrice;
    currentWrap.classList.add('coupon-price-wrap');
    priceEl.classList.add('coupon-price-row');
    priceContainer.classList.add('is-coupon-applied');

    const original = document.createElement('s');
    original.className = 'coupon-original-price';
    original.textContent = originalPrice;

    const discounted = document.createElement('span');
    discounted.className = 'coupon-price';
    discounted.textContent = this.format(this.getDiscountedAmount());

    const rowItems = [discounted, original];

    if (this.dataset.couponCode) {
      const couponCode = document.createElement('span');
      couponCode.className = 'coupon-price-code';
      couponCode.textContent = `with ${this.dataset.couponCode}`;
      rowItems.push(couponCode);
    }

    priceEl.replaceChildren(...rowItems);
    return true;
  }

}

if (!customElements.get('product-coupon-offer')) {
  customElements.define('product-coupon-offer', ProductCouponOffer);
}
